import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '../../../components/button';
import { getMeetingHistory, getMeetingHistoryByParams, isMeetingHistoryLoading, } from '../../../store/features/meeting-history';
import { getMeetingHistoryForCSVByParams } from './account.meeting-history.utils';
import { AnalyticsHelpers } from '../analytics.helpers';
import { selectInstitution, selectUser, canUseExportMeetingHistory, } from '../../../store/features/system';
import { AnalyticsTabs } from '../analytics-tabs';
import { DatepickerBlock } from '../usage/datepicker-block';
import { Modal } from '../../../components';
import { FilterBlock } from '../filter-block';
import { getAnalyticsDatePeriod, getAnalyticsFilter, setAnalyticsFilter, setAnalyticsDatePeriod, } from '../../../store/features/analytics';
import { NoMeetingHistory } from './no-meeting-history';
import { MeetingHistoryTable } from './meeting-history-table';
import { Spinner } from '../../../components/spinner/index';
import './account.meeting-history.scss';
import { useEffectExceptOnMount } from '../../../modules/core';
import { analyticsFilterSelected } from '../analytics.utils';
import { AnalyticsPagesForTracking, DatePeriod } from '../analytics.constants';
const limit = 10;
const exportLimit = 25000;
export const MeetingHistory = () => {
    const data = useSelector(getMeetingHistory);
    const isLoading = useSelector(isMeetingHistoryLoading);
    const dispatch = useDispatch();
    const intl = useIntl();
    const user = useSelector(selectUser);
    const showExportMeetingHistoryButton = useSelector(canUseExportMeetingHistory);
    const institution = useSelector(selectInstitution);
    const datePeriod = useSelector(getAnalyticsDatePeriod);
    const datePickerOptions = AnalyticsHelpers.optionsForDatepicker(intl);
    const cn = AnalyticsHelpers.makeMeetingHistoryClass('block');
    const isUserInstitutionOwner = user.institutionsMemberships.some((im) => im.role === 'owner' && im.institutionId === institution?.id);
    const isUserInstitutionAdmin = user.institutionsMemberships.some((im) => im.role === 'admin' && im.institutionId === institution?.id);
    const isFilterAvailable = institution && (isUserInstitutionAdmin || isUserInstitutionOwner);
    const { providersIds, roomsIds, locationIds } = isFilterAvailable
        ? useSelector(getAnalyticsFilter)
        : { providersIds: [user.id], roomsIds: [], locationIds: [] };
    const [currentPage, setCurrentPage] = useState(1);
    const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
    useEffect(() => {
        const { start, end } = AnalyticsHelpers.createPeriod(datePickerOptions.find((item) => item.key === datePeriod));
        const offset = (currentPage - 1) * limit;
        const [, roomIdFromHash] = (/^#room=(\d+)$/.exec(location.hash || '')) || [];
        if (isFilterAvailable && roomIdFromHash) {
            location.hash = '';
            dispatch(setAnalyticsFilter({
                providersIds: [],
                roomsIds: [Number.parseInt(roomIdFromHash)],
                locationIds: [],
            }));
        }
        else {
            dispatch(getMeetingHistoryByParams({
                start,
                end,
                offset,
                limit,
                providersIds,
                roomsIds,
                locationIds,
            }));
        }
    }, [
        datePeriod,
        currentPage,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
    ]);
    useEffectExceptOnMount(() => {
        dispatch(analyticsFilterSelected({
            page: AnalyticsPagesForTracking.MEETING_HISTORY,
        }));
    }, [
        datePeriod,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
    ]);
    useEffect(() => {
        if (!(datePeriod === DatePeriod.THIS_MONTH &&
            providersIds.length === 0 &&
            roomsIds.length === 0 &&
            locationIds.length === 0)) {
            dispatch(analyticsFilterSelected({
                page: AnalyticsPagesForTracking.MEETING_HISTORY,
            }));
        }
    }, []);
    const onPageChange = useCallback((page, offset) => {
        setCurrentPage(page);
    }, []);
    const selectDate = useCallback((selectedOption) => {
        setCurrentPage(1);
        dispatch(setAnalyticsDatePeriod(selectedOption.key));
    }, []);
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const setFilter = useCallback(({ providersIds, roomsIds, locationIds }) => {
        setCurrentPage(1);
        dispatch(setAnalyticsFilter({ providersIds, roomsIds, locationIds }));
    }, []);
    const downloadMeetingHistoryAndSendTracking = () => {
        const { start, end } = AnalyticsHelpers.createPeriod(datePickerOptions.find((item) => item.key === datePeriod));
        dispatch(getMeetingHistoryForCSVByParams({ start, end, intl }));
    };
    const exportMeetingHistoryButton = useCallback(() => {
        if (data.total > exportLimit) {
            setIsLimitModalOpen(true);
        }
        else {
            downloadMeetingHistoryAndSendTracking();
        }
    }, [
        datePeriod,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
        data.total,
    ]);
    const exportMeetingHistoryLimitButton = useCallback(() => {
        setIsLimitModalOpen(false);
        downloadMeetingHistoryAndSendTracking();
    }, [
        datePeriod,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
        data.total,
    ]);
    return (<div className={cn('wrapper')}>
      {data.total > exportLimit && (<Modal isOpen={isLimitModalOpen} title={(<>
              <FormattedMessage id="account.meeting-history.limit.header"/>
            </>)} onClose={() => setIsLimitModalOpen(false)}>
          <div className={cn('modal-limit')}>
            <div className={cn('modal-limit-message')}>
              <FormattedMessage id="account.meeting-history.limit.message" values={{
                total: new Intl.NumberFormat('en-US').format(data.total),
                limit: new Intl.NumberFormat('en-US').format(exportLimit),
                link: (<a target="_blank" href="https://documenter.getpostman.com/view/11724954/TzRPk9pS#a77b246b-bdb5-425c-94ba-739c5e4a3903" rel="noreferrer">
                      <FormattedMessage id="account.meeting-history.limit.link"/>
                    </a>),
            }}/>
            </div>
            <div className={cn('modal-limit-actions')}>
              <Button onClick={exportMeetingHistoryLimitButton} disabled={!data.total}>
                <FormattedMessage id="account.meeting-history.limit.action"/>
              </Button>
            </div>
          </div>
        </Modal>)}
      <div className={cn('header')}>
        <h3 className={cn('header-text')}>
          <FormattedMessage id="account.analytics.title"/>
        </h3>
        {isFilterAvailable && (<FilterBlock onChange={setFilter} selectedProvidersIds={providersIds} selectedRoomsIds={roomsIds} selectedLocationsIds={locationIds}/>)}
      </div>
      <div className="tabs">
        <AnalyticsTabs />
        <div className="tabs__content">
          <div className={cn('header-wrapper')}>
            {!(datePeriod === DatePeriod.ALL_TIME && data.total === 0) ? (<DatepickerBlock options={datePickerOptions} selectedOptionKey={datePeriod} onChange={selectDate}/>) : (<span />)}
            {showExportMeetingHistoryButton && (<div>
                <button onClick={exportMeetingHistoryButton} disabled={!data.total} className={classnames([cn('meeting-history-export')])}>
                  <svg>
                    <use xlinkHref="#export-users"/>
                  </svg>
                  <FormattedMessage id="account.meeting-history.export"/>
                </button>
              </div>)}
          </div>
          {isLoading && (<Spinner className={cn('spinner')} label={<FormattedMessage id="account.meeting-history.spinner-label"/>} withBlur/>)}
          {data.total > 0 && (<MeetingHistoryTable onPageChange={onPageChange} limit={limit} currentPage={currentPage}/>)}
          {data.total === 0 && !isLoading && <NoMeetingHistory />}
        </div>
      </div>
    </div>);
};
